<script lang="ts">
  import { onMount, untrack } from 'svelte';
  import {
    AreaSeries,
    type IChartApi,
    type ISeriesApi,
    LineSeries,
    LineStyle,
    type Time,
    type UTCTimestamp,
    createChart,
    createSeriesMarkers,
    type SeriesMarker,
  } from 'lightweight-charts';
  import type { VixTimelineRow, VixTradeEvent } from '$lib/api/vix';
  import { themeStore } from '$lib/stores/theme.svelte';
  import {
    type ChartTheme,
    getChartTheme,
    tickMarkFormatterET,
    withAlpha,
  } from '$lib/utils/chart-helpers';

  // ============================================================
  // <VixDualChart> - PR-3 (port del VIX Analysis tab de v1).
  //
  // Dos charts apilados sincronizados via subscribeCrosshairMove:
  //   - top (60%): Cumulative P&L BT-style (area fill verde/rojo segun signo)
  //   - bottom (40%): CBOE VIX (line gold)
  //
  // Idiomatico de Lightweight Charts. Mas legible que dual-axis en panta-
  // llas chicas. Trades como markers sobre el chart de P&L (entry triangle
  // up/down por side; color por win/loss BT).
  //
  // Sincronizacion de crosshairs:
  //   chart A.subscribeCrosshairMove -> chart B.setCrosshairPosition(...) y viceversa.
  //   Para evitar loop infinito, flag #syncing en cada lado.
  //
  // Theme: lee CSS vars via getChartTheme(); el operador puede cambiar
  // rabbit ↔ classic en runtime, los charts se re-renderean.
  // ============================================================

  interface Props {
    timeline: VixTimelineRow[];
    trades: VixTradeEvent[];
    timeframe: '1m' | '5m'; // necesario para calcular bucket exacto (no inferir del timeline)
    height?: number; // total alto del bloque (suma de los 2 charts + gap)
  }

  let { timeline, trades, timeframe, height = 520 }: Props = $props();

  // Bottom chart oculto si el VIX no tiene NINGUN dato (sesion overnight
  // donde Yahoo solo cubre RTH del SPX, etc). En ese caso mostramos un
  // mensaje en el espacio del bottom en lugar de un chart vacio que confunde.
  let hasVixData = $derived(timeline.some((r) => r.vix != null));

  let topContainer: HTMLDivElement;
  // bottomContainer puede ser undefined si !hasVixData (no se renderiza
  // el div). $state asegura reactivity con bind:this.
  let bottomContainer = $state<HTMLDivElement | undefined>();
  let topChart: IChartApi | null = null;
  let bottomChart: IChartApi | null = null;
  let pnlSeries: ISeriesApi<'Area'> | null = null;
  let vixSeries: ISeriesApi<'Line'> | null = null;

  // Altos: 60/40 cuando hay VIX, 100% para el P&L cuando no hay VIX.
  // (sesiones overnight donde Yahoo no tiene bars del VIX).
  let topHeight = $derived(hasVixData ? Math.floor(height * 0.6) : height);
  let bottomHeight = $derived(hasVixData ? height - topHeight - 8 : 0);

  let theme = $state<ChartTheme>(getChartTheme());

  // ----- color del area del P&L segun valor final -----
  let lastPnL = $derived(
    timeline.length > 0 ? timeline[timeline.length - 1]!.pnl : 0,
  );
  let pnlColor = $derived(lastPnL >= 0 ? theme.success : theme.destructive);

  // ----- data series -----
  // Gap threshold: si la diferencia entre dos buckets consecutivos supera
  // este valor, insertamos un "whitespace" point (sin value) que Lightweight
  // dibuja como GAP real - no como linea recta interpolada entre clusters.
  // Para sesiones multi-dia (Lunes a Jueves 18-00), un gap > 1h significa
  // overnight: no queremos que la linea conecte el cierre del Lunes con la
  // apertura del Martes.
  const GAP_THRESHOLD_SEC = 60 * 60; // 1h

  type AreaPoint = { time: UTCTimestamp; value: number } | { time: UTCTimestamp };

  function getPnLData(): AreaPoint[] {
    const out: AreaPoint[] = [];
    const seen = new Set<number>();
    let prevT: number | null = null;
    for (const r of timeline) {
      const t = Math.floor(r.timestamp / 1000);
      if (seen.has(t)) continue;
      seen.add(t);
      // Si hay gap > threshold, inserto whitespace point para forzar gap visual.
      if (prevT !== null && t - prevT > GAP_THRESHOLD_SEC) {
        // Insertamos el whitespace justo despues del prevT (1 bucket de margen).
        out.push({ time: (prevT + 1) as UTCTimestamp });
      }
      out.push({ time: t as UTCTimestamp, value: r.pnl });
      prevT = t;
    }
    return out;
  }

  function getVixData(): AreaPoint[] {
    const out: AreaPoint[] = [];
    const seen = new Set<number>();
    let prevT: number | null = null;
    for (const r of timeline) {
      if (r.vix == null) continue;
      const t = Math.floor(r.timestamp / 1000);
      if (seen.has(t)) continue;
      seen.add(t);
      if (prevT !== null && t - prevT > GAP_THRESHOLD_SEC) {
        out.push({ time: (prevT + 1) as UTCTimestamp });
      }
      out.push({ time: t as UTCTimestamp, value: r.vix });
      prevT = t;
    }
    return out;
  }

  // ----- markers de trades (anclados al bucket del exitTime) -----
  // Bug previo: cuando varios trades caian en el mismo bucket, dibujabamos
  // N markers apilados en el mismo X visualmente confuso. Ahora deduplicamos
  // por bucket: agregamos en grupos {long-win, long-loss, short-win, short-loss}
  // y emitimos hasta 1 marker por grupo por bucket (max 4 por bucket en lugar
  // de N). Si todos los trades del bucket son del mismo grupo -> 1 marker;
  // si son mixtos -> 2-4 markers diferenciados por shape/color/position.
  function getMarkers(): Array<SeriesMarker<UTCTimestamp>> {
    // bucketSec EXACTO derivado del timeframe (no inferir del timeline sparse).
    const bucketSec = timeframe === '1m' ? 60 : 300;

    // Set de buckets validos (donde tenemos un punto en la serie del P&L).
    const validBuckets = new Set<number>();
    for (const r of timeline) validBuckets.add(Math.floor(r.timestamp / 1000));

    // Agrupador: bucket -> {long-win, long-loss, short-win, short-loss} = count
    type Group = 'long-win' | 'long-loss' | 'short-win' | 'short-loss';
    const groups = new Map<number, Set<Group>>();

    for (const t of trades) {
      if (!t.exitTime || !t.side) continue;
      const exitSec = Math.floor(Date.parse(t.exitTime) / 1000);
      if (!Number.isFinite(exitSec)) continue;
      const bucket = Math.floor(exitSec / bucketSec) * bucketSec;
      if (!validBuckets.has(bucket)) continue;

      const key: Group =
        t.side === 'long'
          ? t.profit > 0
            ? 'long-win'
            : 'long-loss'
          : t.profit > 0
            ? 'short-win'
            : 'short-loss';
      if (!groups.has(bucket)) groups.set(bucket, new Set());
      groups.get(bucket)!.add(key);
    }

    const out: Array<SeriesMarker<UTCTimestamp>> = [];
    for (const [bucket, kinds] of groups) {
      for (const k of kinds) {
        const isLong = k.startsWith('long');
        const isWin = k.endsWith('win');
        out.push({
          time: bucket as UTCTimestamp,
          position: isLong ? 'belowBar' : 'aboveBar',
          color: isWin ? theme.success : theme.destructive,
          shape: isLong ? 'arrowUp' : 'arrowDown',
          text: '',
        });
      }
    }
    out.sort((a, b) => (a.time as number) - (b.time as number));
    return out;
  }

  // ----- tooltip enriquecido (solo en chart top) -----
  let tooltipVisible = $state(false);
  let tooltipContent = $state<{ time: string; pnl: number; vix: number | null } | null>(null);
  let tooltipX = $state(0);
  let tooltipY = $state(0);

  // ----- day-change chips (gold) sobre el eje X de AMBOS charts -----
  // Sesiones multi-dia (Lunes a Jueves del mismo bracket) o overnight que
  // cruzan medianoche -> mostramos un chip gold ("Jun 02") arriba del eje X
  // en la coordenada exacta del primer bucket del nuevo dia ET. Se renderea
  // en ambos charts independientemente porque cada uno tiene su propio eje X
  // (pueden tener visibleRange distinto si el usuario hizo zoom asimetrico).
  let topDayChips = $state<Array<{ x: number; label: string }>>([]);
  let bottomDayChips = $state<Array<{ x: number; label: string }>>([]);

  const ET_DAY_KEY_FMT = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const ET_DAY_LABEL_FMT = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    month: 'short',
    day: '2-digit',
  });

  function etDayKey(ms: number): string {
    return ET_DAY_KEY_FMT.format(new Date(ms));
  }

  // Recalcula la X de cada cambio de dia ET visible para AMBOS charts.
  // Necesitamos timestamps DISTINTOS para top y bottom: timeToCoordinate de
  // Lightweight Charts devuelve null si el time NO esta en la serie del chart
  // donde se llama. Como P&L y VIX tienen sets distintos, buscamos:
  //   - top: primer bucket del dia con dato de P&L (todos los buckets del
  //     timeline son del P&L, pero por compatibilidad usamos todos los keys)
  //   - bottom: primer bucket del dia con dato de VIX (r.vix != null)
  function recomputeDayChips() {
    if (!topChart || timeline.length === 0) {
      topDayChips = [];
      bottomDayChips = [];
      return;
    }
    // Para el top: primer bucket de cada dia (con P&L, que es todos).
    const topFirstByDay = new Map<string, number>();
    // Para el bottom: primer bucket de cada dia CON VIX no-null.
    const bottomFirstByDay = new Map<string, number>();
    for (const r of timeline) {
      const key = etDayKey(r.timestamp);
      const ts = Math.floor(r.timestamp / 1000);
      if (!topFirstByDay.has(key)) topFirstByDay.set(key, ts);
      if (r.vix != null && !bottomFirstByDay.has(key)) bottomFirstByDay.set(key, ts);
    }

    const topDays = [...topFirstByDay.entries()];
    const bottomDays = [...bottomFirstByDay.entries()];

    const topChips: Array<{ x: number; label: string }> = [];
    const bottomChips: Array<{ x: number; label: string }> = [];

    // Skip el primer dia de CADA chart (es el "inicio" natural, no necesita chip).
    for (let i = 1; i < topDays.length; i++) {
      const [, ts] = topDays[i]!;
      const label = ET_DAY_LABEL_FMT.format(new Date(ts * 1000)).toUpperCase();
      const x = topChart.timeScale().timeToCoordinate(ts as UTCTimestamp);
      if (x !== null) topChips.push({ x, label });
    }
    if (bottomChart) {
      for (let i = 1; i < bottomDays.length; i++) {
        const [, ts] = bottomDays[i]!;
        const label = ET_DAY_LABEL_FMT.format(new Date(ts * 1000)).toUpperCase();
        const x = bottomChart.timeScale().timeToCoordinate(ts as UTCTimestamp);
        if (x !== null) bottomChips.push({ x, label });
      }
    }
    topDayChips = topChips;
    bottomDayChips = bottomChips;
  }

  const ET_FMT = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  // ----- crosshair sync entre top y bottom -----
  let isSyncing = false;

  // Tooltip del top chart - SIEMPRE activo, independiente del VIX.
  function setupTooltip() {
    if (!topChart || !pnlSeries) return;
    topChart.subscribeCrosshairMove((param) => {
      if (!param.point || param.point.x < 0 || param.point.y < 0 || !param.time) {
        tooltipVisible = false;
      } else {
        const v = param.seriesData.get(pnlSeries!) as { value: number } | undefined;
        if (v) {
          const t = param.time as number;
          // VIX del mismo bucket (puede ser null en sesiones overnight).
          let vix: number | null = null;
          for (const r of timeline) {
            if (Math.floor(r.timestamp / 1000) === t) {
              vix = r.vix;
              break;
            }
          }
          tooltipContent = {
            time: ET_FMT.format(new Date(t * 1000)),
            pnl: v.value,
            vix,
          };
          tooltipVisible = true;
          tooltipX = param.point.x + 12;
          tooltipY = param.point.y + 12;
        }
      }
      // Sync bottom solo si existe. setCrosshairPosition tira "Value is null"
      // si VIX no tiene data en ese time exacto, asi que try/catch silencioso.
      if (!bottomChart || !vixSeries) return;
      if (isSyncing) return;
      isSyncing = true;
      try {
        if (param.time != null && getVixData().length > 0) {
          try {
            bottomChart.setCrosshairPosition(NaN, param.time, vixSeries);
          } catch {
            /* VIX sin punto en ese time - silencio, no es error operativo */
          }
        } else {
          bottomChart.clearCrosshairPosition();
        }
      } finally {
        isSyncing = false;
      }
    });
  }

  // Solo se llama cuando bottomChart existe (hay VIX data).
  function setupSync() {
    if (!topChart || !bottomChart || !pnlSeries || !vixSeries) return;

    bottomChart.subscribeCrosshairMove((param) => {
      if (isSyncing) return;
      isSyncing = true;
      try {
        if (param.time != null && getPnLData().length > 0) {
          try {
            topChart!.setCrosshairPosition(NaN, param.time, pnlSeries!);
          } catch {
            /* P&L sin data en ese time */
          }
        } else {
          topChart!.clearCrosshairPosition();
        }
      } finally {
        isSyncing = false;
      }
    });

    // Sync de zoom/pan. setVisibleLogicalRange puede tirar "Value is null"
    // cuando los datasets tienen tamanos muy distintos -> try/catch silencioso.
    topChart.timeScale().subscribeVisibleLogicalRangeChange((range) => {
      if (!range || !bottomChart) return;
      try {
        bottomChart.timeScale().setVisibleLogicalRange(range);
      } catch {
        /* mismatch de rango entre datasets - no crittico */
      }
    });
    bottomChart.timeScale().subscribeVisibleLogicalRangeChange((range) => {
      if (!range || !topChart) return;
      try {
        topChart.timeScale().setVisibleLogicalRange(range);
      } catch {
        /* mismatch */
      }
    });
  }

  function createChartCommon(container: HTMLDivElement, h: number): IChartApi {
    const initialWidth = container.clientWidth > 0 ? container.clientWidth : 600;
    return createChart(container, {
      width: initialWidth,
      height: h,
      layout: {
        background: { color: 'transparent' },
        textColor: theme.foreground,
        fontFamily: 'Inter, system-ui, sans-serif',
      },
      grid: {
        vertLines: { color: theme.grid, visible: false },
        horzLines: { color: theme.grid, visible: true },
      },
      rightPriceScale: { borderColor: theme.border, autoScale: true },
      timeScale: {
        borderColor: theme.border,
        timeVisible: true,
        secondsVisible: false,
        tickMarkFormatter: (time: Time, tickMarkType: number) =>
          tickMarkFormatterET(time as number, tickMarkType),
      },
      crosshair: { mode: 1 },
      handleScroll: { mouseWheel: false, pressedMouseMove: true },
      handleScale: { mouseWheel: true, pinch: true, axisPressedMouseMove: true },
    });
  }

  onMount(() => {
    theme = getChartTheme();

    // ----- top chart: P&L acumulado -----
    topChart = createChartCommon(topContainer, topHeight);
    pnlSeries = topChart.addSeries(AreaSeries, {
      lineColor: pnlColor,
      topColor: withAlpha(pnlColor, 0.33),
      bottomColor: withAlpha(pnlColor, 0),
      lineWidth: 2,
      priceLineVisible: false,
      lastValueVisible: true,
    });
    // Zero line dashed - referencia visual constante.
    pnlSeries.createPriceLine({
      price: 0,
      color: theme.muted,
      lineWidth: 1,
      lineStyle: LineStyle.Dashed,
      axisLabelVisible: false,
      title: '',
    });

    // ----- bottom chart: VIX (solo si hay datos) -----
    // Sesiones overnight donde Yahoo no cubre el horario -> no creamos el
    // chart, mostramos un mensaje en el div en su lugar (ver template).
    if (hasVixData && bottomContainer) {
      bottomChart = createChartCommon(bottomContainer, bottomHeight);
      vixSeries = bottomChart.addSeries(LineSeries, {
        color: theme.gold,
        lineWidth: 2,
        priceLineVisible: false,
        lastValueVisible: true,
      });
      setupSync();
    }

    // Tooltip del top SIEMPRE se setea (independiente de VIX).
    setupTooltip();

    // Day-change chips: recomputar cuando cambia el rango visible o el size
    // de cualquiera de los charts. Cada chart tiene su propio eje X (el sync
    // de logical range los empareja pero igual disparan eventos separados).
    topChart.timeScale().subscribeVisibleTimeRangeChange(() => recomputeDayChips());
    topChart.timeScale().subscribeSizeChange(() => recomputeDayChips());
    if (bottomChart) {
      bottomChart.timeScale().subscribeVisibleTimeRangeChange(() => recomputeDayChips());
      bottomChart.timeScale().subscribeSizeChange(() => recomputeDayChips());
    }

    refreshData();

    // ResizeObserver: replica width entre los dos charts
    const ro = new ResizeObserver(() => {
      if (topChart && topContainer) topChart.applyOptions({ width: topContainer.clientWidth });
      if (bottomChart && bottomContainer)
        bottomChart.applyOptions({ width: bottomContainer.clientWidth });
    });
    ro.observe(topContainer);
    if (bottomContainer) ro.observe(bottomContainer);

    return () => {
      ro.disconnect();
      topChart?.remove();
      bottomChart?.remove();
      topChart = null;
      bottomChart = null;
    };
  });

  function refreshData() {
    if (!topChart || !pnlSeries) return;
    // biome-ignore lint/suspicious/noExplicitAny: Lightweight types
    pnlSeries.setData(getPnLData() as any);
    pnlSeries.applyOptions({
      lineColor: pnlColor,
      topColor: withAlpha(pnlColor, 0.33),
      bottomColor: withAlpha(pnlColor, 0),
    });
    // Markers: API v5 = createSeriesMarkers
    createSeriesMarkers(pnlSeries, getMarkers());
    topChart.timeScale().fitContent();

    // VIX solo si existe (sesiones diurnas con overlap RTH).
    if (bottomChart && vixSeries) {
      // biome-ignore lint/suspicious/noExplicitAny: Lightweight types
      vixSeries.setData(getVixData() as any);
      bottomChart.timeScale().fitContent();
    }

    // Recompute initial day-change chips (fitContent ya dejo coordenadas estables).
    recomputeDayChips();
  }

  $effect(() => {
    void timeline;
    void trades;
    untrack(() => refreshData());
  });

  $effect(() => {
    void themeStore.theme;
    untrack(() => {
      if (!topChart) return;
      theme = getChartTheme();
      const commonLayout = {
        layout: { background: { color: 'transparent' }, textColor: theme.foreground },
        grid: { horzLines: { color: theme.grid } },
        rightPriceScale: { borderColor: theme.border },
        timeScale: { borderColor: theme.border },
      };
      topChart.applyOptions(commonLayout);
      bottomChart?.applyOptions(commonLayout);
      vixSeries?.applyOptions({ color: theme.gold });
      refreshData();
    });
  });

  function fmtPnL(n: number): string {
    if (n === 0) return '$0.00';
    const sign = n > 0 ? '+' : '-';
    return `${sign}$${Math.abs(n).toFixed(2)}`;
  }
</script>

<div class="relative w-full" style="height: {height}px;">
  <!-- Top: P&L acumulado -->
  <div
    bind:this={topContainer}
    class="absolute inset-x-0 top-0"
    style="height: {topHeight}px;"
  ></div>

  <!-- Bottom: VIX si hay datos, sino mensaje -->
  {#if hasVixData}
    <div
      bind:this={bottomContainer}
      class="absolute inset-x-0 bottom-0"
      style="height: {bottomHeight}px;"
    ></div>
  {/if}

  <!-- Day-change chips (gold) sobre el eje X de AMBOS charts. Posicionados
       en la coordenada X exacta del primer bucket del nuevo dia ET. Resaltan
       visualmente los rollover de dia en sesiones multi-dia / overnight.
       El bottom chart tiene su propio set (puede tener distinto visibleRange
       si el usuario hizo zoom independiente). -->
  {#each topDayChips as chip, i (i)}
    <div
      class="pointer-events-none absolute z-10 -translate-x-1/2 select-none rounded-md border border-gold/50 bg-gold/15 px-1.5 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider text-gold backdrop-blur-sm"
      style="left: {chip.x}px; top: {topHeight - 28}px;"
    >
      {chip.label}
    </div>
  {/each}
  {#if hasVixData}
    {#each bottomDayChips as chip, i (i)}
      <!-- top = inicio del bottom chart + alto del bottom - margen para que
           el chip flote JUSTO ARRIBA del eje X del bottom (no debajo).
           bottom chart arranca en (topHeight + 8 gap) y mide bottomHeight,
           el eje X esta al final - resto 28px para que el chip lo solape. -->
      <div
        class="pointer-events-none absolute z-10 -translate-x-1/2 select-none rounded-md border border-gold/50 bg-gold/15 px-1.5 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider text-gold backdrop-blur-sm"
        style="left: {chip.x}px; top: {topHeight + 8 + bottomHeight - 28}px;"
      >
        {chip.label}
      </div>
    {/each}
  {/if}

  <!-- Tooltip enriquecido (overlay sobre el top chart) -->
  {#if tooltipVisible && tooltipContent}
    <div
      class="pointer-events-none absolute z-10 rounded-lg border border-border bg-surface-3/95 px-3 py-2 shadow-luxury backdrop-blur lifted-edge"
      style="left: {tooltipX}px; top: {tooltipY}px;"
    >
      <div class="text-muted-foreground text-[10px] uppercase tracking-wider">
        {tooltipContent.time} ET
      </div>
      <div class="mt-1 flex items-baseline gap-3">
        <div>
          <div class="text-muted-foreground text-[9px] uppercase tracking-wider">P&L</div>
          <div
            class={[
              'font-mono text-sm font-semibold tabular-nums',
              tooltipContent.pnl >= 0 ? 'text-success' : 'text-destructive',
            ].join(' ')}
          >
            {fmtPnL(tooltipContent.pnl)}
          </div>
        </div>
        {#if tooltipContent.vix != null}
          <div>
            <div class="text-muted-foreground text-[9px] uppercase tracking-wider">VIX</div>
            <div class="text-gold font-mono text-sm font-semibold tabular-nums">
              {tooltipContent.vix.toFixed(2)}
            </div>
          </div>
        {/if}
      </div>
    </div>
  {/if}
</div>
